import { useEffect } from 'react';
import type { SeoPageConfig } from '../types/Seo';
import { resolvePageJsonLd } from '../seo/pageJsonLd';
import { SEO_DEFAULT_OG_IMAGE } from '../seo/site';

function setMeta(name: string, content: string, property = false) {
  const selector = property
    ? `meta[property="${name}"]`
    : `meta[name="${name}"]`;
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement('meta');
    if (property) tag.setAttribute('property', name);
    else tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setCanonical(url: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
}

export function useSeoMeta({
  title,
  description,
  path,
  h1,
  summary,
  image = SEO_DEFAULT_OG_IMAGE,
  noindex = false,
  schema = null,
}: SeoPageConfig) {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL?.trim() || window.location.origin;
    const normalizedPath = path || window.location.pathname;
    const canonical = new URL(normalizedPath, siteUrl).toString();
    const imageUrl = new URL(image, siteUrl).toString();

    document.title = title;
    setMeta('description', description);
    setMeta('robots', noindex ? 'noindex,nofollow' : 'index,follow');
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', 'website', true);
    setMeta('og:url', canonical, true);
    setMeta('og:image', imageUrl, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', imageUrl);
    setCanonical(canonical);

    document.getElementById('seo-schema')?.remove();

    const ld = resolvePageJsonLd(
      { title, description, path, h1, summary, image, noindex, schema },
      siteUrl,
    );
    const pageEl = document.getElementById('seo-schema-page');
    if (ld) {
      const json = JSON.stringify(ld);
      if (pageEl instanceof HTMLScriptElement) {
        pageEl.textContent = json;
      } else {
        pageEl?.remove();
        const script = document.createElement('script');
        script.id = 'seo-schema-page';
        script.type = 'application/ld+json';
        script.textContent = json;
        document.head.appendChild(script);
      }
    } else {
      pageEl?.remove();
    }
  }, [description, h1, image, noindex, path, schema, summary, title]);
}
